/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useState, useContext} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Heading,
    SimpleGrid,
    Skeleton as ChakraSkeleton,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '../../hooks/use-multi-site'
import {useToast} from '../../hooks/use-toast'
import {useForm} from 'react-hook-form'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../components/toggle-card'
import FieldsetForm from '../../components/shared/ui/FieldsetForm'
import ProfileFields from '../../components/forms/profile-fields'
import UpdatePasswordFields from '../../components/forms/update-password-fields'
import FormActionButtons from '../../components/forms/form-action-buttons'
import {
    useShopperCustomersMutation,
    useAuthHelper,
    AuthHelpers,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAlertMgr} from '../../hooks/use-alert-mgr'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSplioService} from '../../hooks/use-splio-service'
import {
    extractPhone,
    findDialCodeByCountry,
    addPrefixToKeys,
    removePrefixFromKeys
} from './../../utils/utils'
import {OUTLET_DISPATCH_URL, REGEX} from '../../constants'
import {IPContext} from '../../custom-contexts'
import {useHistory} from 'react-router-dom'
import {useXSSProtection} from './../../hooks/use-xss-protection.js'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * This is a specialized Skeleton component that which uses the customers authtype as the
 * `isLoaded` state. It also will revert it's provided size (height, width) when the loaded
 * state changes. This allows you to have skeletons of a specific size, but onece loaded
 * the bounding element will affect the contents size.
 */
// eslint-disable-next-line react/prop-types
const Skeleton = forwardRef(({children, height, width, ...rest}, ref) => {
    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const size = !isRegistered
        ? {
              height,
              width
          }
        : {}
    return (
        <ChakraSkeleton ref={ref} isLoaded={!customer.isLoading} {...rest} {...size}>
            {children}
        </ChakraSkeleton>
    )
})

Skeleton.displayName = 'Skeleton'

const NotProvidedMessage = (
    <FormattedMessage defaultMessage="Not provided" id="profile_card.message.not_provided" />
)

const ProfileCard = ({allowPasswordChange = false}) => {
    const {locale, buildUrl, isOutletSite} = useMultiSite()
    const {forwardAccountToSplio} = useSplioService()
    const {formatMessage, formatDate} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const [currentCustomer, setCurrentCustomer] = useState(customer)
    const {isRegistered, customerId} = customer

    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')

    const toast = useToast()
    const [isEditing, setIsEditing] = useState(false)
    const [isBirthdayReset, setBirthdayReset] = useState(false)
    const {isIPBlocked} = useContext(IPContext)
    const profileFormPrefix = 'profile_'
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()
    const {usePageLoadAndCookies} = useGTM()

    useEffect(() => {
        setCurrentCustomer(customer)

        if (customer?.c_marketingConsent) {
            forwardAccountToSplio({
                email: customer?.email,
                firstName: customer?.firstName,
                lastName: customer?.lastName,
                phoneHome: customer?.phoneHome,
                birthday: customer?.birthday,
                marketingConsent: customer?.c_marketingConsent,
                newsletterSection: 'header',
                requestType: 'profile'
            })
        }
    }, [customer?.authType])

    const form = useForm({
        defaultValues: addPrefixToKeys(
            {
                firstName: currentCustomer?.firstName,
                lastName: currentCustomer?.lastName,
                email: currentCustomer?.email,
                phone: extractPhone(REGEX.PHONE, currentCustomer?.phoneHome)?.phone,
                birthday: currentCustomer?.birthday
            },
            profileFormPrefix
        )
    })

    useEffect(() => {
        form.reset(
            addPrefixToKeys(
                {
                    firstName: currentCustomer.firstName,
                    lastName: currentCustomer.lastName,
                    email: currentCustomer.email,
                    phone: extractPhone(REGEX.PHONE, currentCustomer?.phoneHome)?.phone,
                    birthday: currentCustomer?.birthday
                },
                profileFormPrefix
            )
        )
    }, [
        currentCustomer?.firstName,
        currentCustomer?.lastName,
        currentCustomer?.email,
        currentCustomer?.phoneHome,
        currentCustomer?.birthday
    ])

    useEffect(() => {
        if (isBirthdayReset) {
            form.setValue(`${profileFormPrefix}birthday`, currentCustomer?.birthday)
            setBirthdayReset(false)
        }
    }, [isBirthdayReset])

    /**
     * Sends updated customer information to the Splio service if marketing consent is given.
     *
     * This asynchronous function checks if the `c_marketingConsent` property of the
     * `updatedCustomer` object is true. If so, it forwards the customer's account
     * information to the Splio service by calling the `forwardAccountToSplio` function.
     *
     * @async
     * @function fetchSplioCall
     * @param {Object} updatedCustomer - The updated customer object containing user details.
     * @param {string} updatedCustomer.email - The email address of the customer.
     * @param {string} updatedCustomer.firstName - The first name of the customer.
     * @param {string} updatedCustomer.lastName - The last name of the customer.
     * @param {string} updatedCustomer.phoneHome - The home phone number of the customer.
     * @param {string} updatedCustomer.birthday - The birthday of the customer.
     */
    const fetchSplioCall = async (updatedCustomer) => {
        if (updatedCustomer.c_marketingConsent) {
            await forwardAccountToSplio({
                email: updatedCustomer?.email,
                firstName: updatedCustomer?.firstName,
                lastName: updatedCustomer?.lastName,
                phoneHome: updatedCustomer?.phoneHome,
                birthday: updatedCustomer?.birthday,
                newsletterSection: 'body',
                requestType: 'profile'
            })
        }
    }

    const submit = async (data) => {
        try {
            form.clearErrors()

            if (isIPBlocked) {
                const path = buildUrl(isOutletSite ? OUTLET_DISPATCH_URL : '/')
                history.push(path)
                return
            }

            const values = removePrefixFromKeys(data, profileFormPrefix)

            const isXssDetected = validateXssForm(values)
            if (isXssDetected) {
                return
            }

            updateCustomerMutation.mutate(
                {
                    parameters: {customerId},
                    body: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        // phone format: phone country dial & phone divided whitespace
                        phoneHome: values.phone
                            ? `${findDialCodeByCountry(values.phoneCountry)} ${values.phone}`.trim()
                            : '',
                        c_phoneHomeCountryCode: values.phone ? values.phoneCountry : '',
                        // no way to send 'null' date
                        // no change birthday value in BM if user reset birthday value in empty
                        ...(values.birthday && {birthday: values.birthday}),
                        // NOTE/ISSUE
                        // The sdk is allowing you to change your email to an already-existing email.
                        // I would expect an error. We also want to keep the email and login the same
                        // for the customer, but the sdk isn't changing the login when we submit an
                        // updated email. This will lead to issues where you change your email but end
                        // up not being able to login since 'login' will no longer match the email.
                        email: values.email,
                        login: values.email,
                        // preferred locale is updated on account edit with the site locale
                        preferredLocale: locale.id.replace('-', '_')
                    }
                },
                {
                    onSuccess: (updatedCustomer) => {
                        fetchSplioCall(updatedCustomer)
                        setCurrentCustomer(updatedCustomer)
                        setIsEditing(false)

                        // Reset the birthday field if the birthday was filled as empty by user
                        setBirthdayReset(!values.birthday)

                        toast({
                            description: formatMessage({
                                defaultMessage: 'Profile updated',
                                id: 'profile_card.info.profile_updated'
                            }),
                            status: 'success'
                        })
                    }
                }
            )
        } catch (error) {
            form.setError('global', {type: 'manual', message: error.message})
        }
    }

    usePageLoadAndCookies('account')

    return (
        <ToggleCard
            id="my-profile"
            title={
                <Skeleton height="14px" width="120px">
                    <FormattedMessage
                        defaultMessage="My Profile"
                        id="profile_card.title.my_profile"
                    />
                </Skeleton>
            }
            editing={isEditing}
            disableEdit={!allowPasswordChange}
            isTitleFocusOn={false}
            isLoading={form.formState.isSubmitting}
            onEdit={isRegistered ? () => setIsEditing(true) : undefined}
            layerStyle="accountCard"
        >
            <ToggleCardEdit>
                <FieldsetForm
                    legendText={formatMessage({
                        defaultMessage: 'Edit profile form',
                        id: 'profile_form.form.legend'
                    })}
                    onSubmit={form.handleSubmit(submit)}
                    error={form.formState.errors?.global?.message}
                >
                    <Stack spacing={24}>
                        <ProfileFields
                            form={form}
                            countryCode={currentCustomer?.c_phoneHomeCountryCode}
                        />
                        <FormActionButtons
                            onCancel={() => {
                                setIsEditing(false)
                                form.reset()
                            }}
                        />
                    </Stack>
                </FieldsetForm>
            </ToggleCardEdit>
            <ToggleCardSummary>
                <Skeleton height={['200px', '200px', '200px', '100px']} width="full">
                    {/* ADA Compliance: dl structure */}
                    <SimpleGrid as="dl" columns={[1, 1, 1, 3]} spacingX={24}>
                        {/* Full Name Info: 1 column - 1 & 2 rows on large */}
                        <Text
                            as="dt"
                            variant="bodyLarge2"
                            gridColumn={{lg: 1}}
                            gridRow={{lg: 1}}
                            mb={4}
                        >
                            <FormattedMessage
                                defaultMessage="Full Name:"
                                id="profile_card.label.full_name"
                            />
                        </Text>
                        <Text
                            as="dd"
                            variant="bodyBase2"
                            color="darkGray"
                            gridColumn={{lg: 1}}
                            gridRow={{lg: 2}}
                            mb={[16, 16, 16, 24]}
                        >
                            {currentCustomer?.firstName} {currentCustomer?.lastName}
                        </Text>

                        {/* Email Address Info: 2 column - 1 & 2 rows on large */}
                        <Text
                            as="dt"
                            variant="bodyLarge2"
                            gridColumn={{lg: 2}}
                            gridRow={{lg: 1}}
                            mb={4}
                        >
                            <FormattedMessage
                                defaultMessage="Email Address:"
                                id="profile_card.label.email"
                            />
                        </Text>
                        <Text
                            as="dd"
                            variant="bodyBase2"
                            color="darkGray"
                            gridColumn={{lg: 2}}
                            gridRow={{lg: 2}}
                            mb={[16, 16, 16, 24]}
                        >
                            {currentCustomer?.email}
                        </Text>

                        {/* Phone Number Info: 3 column - 1 & 2 rows on large */}
                        <Text
                            as="dt"
                            variant="bodyLarge2"
                            gridColumn={{lg: 3}}
                            gridRow={{lg: 1}}
                            mb={4}
                        >
                            <FormattedMessage
                                defaultMessage="Phone Number:"
                                id="profile_card.label.phone"
                            />
                        </Text>
                        <Text
                            as="dd"
                            variant="bodyBase2"
                            color="darkGray"
                            gridColumn={{lg: 3}}
                            gridRow={{lg: 2}}
                            mb={[16, 16, 16, 24]}
                        >
                            {currentCustomer?.phoneHome || NotProvidedMessage}
                        </Text>

                        {/* Date of Birth Info: 1 column - 3 & 4 rows on large */}
                        <Text
                            as="dt"
                            variant="bodyLarge2"
                            gridColumn={{lg: 1}}
                            gridRow={{lg: 3}}
                            mb={4}
                        >
                            <FormattedMessage
                                defaultMessage="Date of Birth:"
                                id="profile_card.label.birthday"
                            />
                        </Text>
                        <Text
                            as="dt"
                            variant="bodyBase2"
                            color="darkGray"
                            gridColumn={{lg: 1}}
                            gridRow={{lg: 4}}
                        >
                            {currentCustomer?.birthday
                                ? formatDate(new Date(currentCustomer.birthday), {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                  })
                                : NotProvidedMessage}
                        </Text>
                    </SimpleGrid>
                </Skeleton>
            </ToggleCardSummary>
        </ToggleCard>
    )
}

/**
 * Defines the prop types for the ProfileCard component.
 *
 * @property {boolean} allowPasswordChange - A flag indicating whether the user is allowed to change their password.
 */
ProfileCard.propTypes = {
    allowPasswordChange: PropTypes.bool
}

const PasswordCard = () => {
    const {formatMessage} = useIntl()
    const {data: customer} = useCurrentCustomer()
    const {isRegistered, email} = customer

    // Here we use AuthHelpers.UpdateCustomerPassword rather than invoking the updateCustomerPassword mutation directly
    // because the AuthHelper will re-authenticate the user's current session after the password change.
    const updateCustomerPassword = useAuthHelper(AuthHelpers.UpdateCustomerPassword)
    const toast = useToast()
    const [isEditing, setIsEditing] = useState(false)

    const form = useForm()
    const passwordFormPrefix = 'update-pass_'

    const submit = async (data) => {
        try {
            const values = removePrefixFromKeys(data, passwordFormPrefix)

            form.clearErrors()
            await updateCustomerPassword.mutateAsync({
                customer,
                password: values.password,
                currentPassword: values.currentPassword,
                shouldReloginCurrentSession: true
            })
            setIsEditing(false)
            toast({
                title: formatMessage({
                    defaultMessage: 'Password updated',
                    id: 'password_card.info.password_updated'
                }),
                status: 'success'
            })
            form.reset()
        } catch (error) {
            const resObj = await error.response.json()
            form.setError('root.global', {type: 'manual', message: resObj.detail})
        }
    }

    return (
        <ToggleCard
            id="password"
            title={
                <Skeleton height="14px" width="120px">
                    <FormattedMessage defaultMessage="Password" id="password_card.title.password" />
                </Skeleton>
            }
            editing={isEditing}
            isTitleFocusOn={false}
            isLoading={form.formState.isSubmitting}
            onEdit={isRegistered ? () => setIsEditing(true) : undefined}
            layerStyle="accountCard"
        >
            <ToggleCardEdit>
                <FieldsetForm
                    legendText={formatMessage({
                        defaultMessage: 'Change password form',
                        id: 'change_password_form.legend'
                    })}
                    onSubmit={form.handleSubmit(submit)}
                    error={form.formState.errors?.root?.global?.message}
                >
                    <Stack spacing={24}>
                        {/* Hidden Username Field for Accessibility:
                            - for password managers  */}
                        <input
                            type="text"
                            name="username"
                            defaultValue={email}
                            autoComplete="username"
                            aria-hidden="true"
                            style={{display: 'none'}}
                        />

                        <UpdatePasswordFields form={form} />
                        <FormActionButtons
                            onCancel={() => {
                                setIsEditing(false)
                                form.reset()
                            }}
                        />
                    </Stack>
                </FieldsetForm>
            </ToggleCardEdit>
            <ToggleCardSummary>
                <Skeleton height="40px" width="full">
                    {/* ADA Compliance: dl structure */}
                    <SimpleGrid as="dl" columns={[1, 1, 1, 3]} spacingX={24}>
                        <Text
                            as="dt"
                            variant="bodyLarge2"
                            gridColumn={{lg: 1}}
                            gridRow={{lg: 1}}
                            mb={4}
                        >
                            <FormattedMessage
                                defaultMessage="Password:"
                                id="password_card.label.password"
                            />
                        </Text>
                        <Text as="dd" variant="bodyBase2" gridColumn={{lg: 1}} gridRow={{lg: 2}}>
                            &bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;
                        </Text>
                    </SimpleGrid>
                </Skeleton>
            </ToggleCardSummary>
        </ToggleCard>
    )
}

const AccountDetail = () => {
    const {isExternal} = useCustomerType()

    const config = getConfig()
    const {sendAlert} = useAlertMgr()
    const [isTestMode, setIsTestMode] = useState(false)

    useEffect(() => {
        setIsTestMode(config.app.ALERT_MGR_TEST_MODE === 'true')
    }, [])

    async function fetchTestAlert1() {
        const testAlertResult1 = await sendAlert({
            context: {
                siteId: 'test-call',
                message: 'test-call',
                category: 'test_call_1',
                order: 'test-call',
                page: 'test-call',
                job: 'test-call',
                asset: 'test-call',
                identifier: 'test-call',
                fileName: 'test-call',
                lineNumber: 'test-call'
            },
            alertType: 'test-call',
            logging: 'test-call'
        })
        return testAlertResult1
    }

    async function fetchTestAlert2() {
        const testAlertResult2 = await sendAlert({
            context: {
                siteId: 'test-call',
                message: 'test-call',
                category: 'test_call_2',
                order: 'test-call',
                page: 'test-call',
                job: 'test-call',
                asset: 'test-call',
                identifier: 'test-call',
                fileName: 'test-call',
                lineNumber: 'test-call'
            },
            alertType: 'test-call',
            logging: 'test-call'
        })
        return testAlertResult2
    }

    async function fetchTestAlert3() {
        const testAlertResult3 = await sendAlert({
            context: {
                siteId: 'test-call',
                message: 'test-call',
                category: 'test_call_3',
                order: 'test-call',
                page: 'test-call',
                job: 'test-call',
                asset: 'test-call',
                identifier: 'test-call',
                fileName: 'test-call',
                lineNumber: 'test-call'
            },
            alertType: 'test-call',
            logging: 'test-call'
        })
        return testAlertResult3
    }

    useEffect(() => {
        if (isTestMode) {
            const testAlert = async () => {
                await fetchTestAlert1()
                await fetchTestAlert2()
                await fetchTestAlert3()
            }

            testAlert()
        }
    }, [isTestMode])

    return (
        <Stack data-testid="account-detail-page" spacing={24}>
            <Box>
                <Heading
                    as="h1"
                    display="inline-block"
                    size="3xl"
                    textTransform="uppercase"
                    tabIndex="0"
                >
                    <FormattedMessage
                        defaultMessage="Personal Information"
                        id="account_detail.title.personal_information"
                    />
                </Heading>
            </Box>

            <Stack spacing={24}>
                <ProfileCard allowPasswordChange={!isExternal} />
                {!isExternal && <PasswordCard />}
            </Stack>
        </Stack>
    )
}

AccountDetail.getTemplateName = () => 'account-detail'

export default AccountDetail
